/**
 * Commission Board — shared between Product Installation & Client Tracker.
 * RM5 per New Client, RM2.50 per Existing Client (status "Done" only).
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { DollarSign, Loader2, Download } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const csvEscape = (v) => {
  const s = v === null || v === undefined ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const downloadCSV = (filename, header, rows) => {
  const csv = [header, ...rows].map((r) => r.map(csvEscape).join(',')).join('\n');
  const url = URL.createObjectURL(new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const monthLabel = (m) => (m ? format(new Date(m + '-01'), 'MMMM yyyy') : '');

export default function CommissionBoardModal({ open, onOpenChange, ibId, ibName }) {
  const [month, setMonth] = useState('');
  const [rows, setRows] = useState([]);
  const [details, setDetails] = useState([]);
  const [rates, setRates] = useState({ new_client: 5, existing_client: 2.5, recheck_tv: 5 });
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async (m) => {
    if (!ibId) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = m ? `?month=${m}` : '';
      const res = await axios.get(`${API}/api/product-installations/${ibId}/commission-summary${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows(res.data.summary || []);
      setDetails(res.data.details || []);
      if (res.data.rates) setRates(res.data.rates);
    } catch (e) {
      setRows([]);
      setDetails([]);
    } finally {
      setLoading(false);
    }
  }, [ibId]);

  useEffect(() => { if (open) fetchData(month); /* eslint-disable-next-line */ }, [open]);

  const total = rows.reduce((s, r) => s + (r.commission || 0), 0);
  const slug = `${(ibName || ibId || 'channel').replace(/\s+/g, '-')}_${month || 'all-time'}`;

  const exportSummary = () => {
    if (!rows.length) { toast.error('Tiada data untuk export'); return; }
    downloadCSV(
      `commission-summary_${slug}.csv`,
      ['Admin', 'Total Closed', 'New Client', 'Existing Client', 'Recheck TV', 'New RM', 'Existing RM', 'Recheck RM', 'Total RM'],
      [
        ...rows.map((r) => [r.admin, r.count, r.new_count, r.existing_count, r.recheck_count || 0,
          (r.new_commission || 0).toFixed(2), (r.existing_commission || 0).toFixed(2),
          (r.recheck_commission || 0).toFixed(2), (r.commission || 0).toFixed(2)]),
        ['TOTAL', rows.reduce((s, r) => s + r.count, 0), rows.reduce((s, r) => s + r.new_count, 0),
          rows.reduce((s, r) => s + r.existing_count, 0),
          rows.reduce((s, r) => s + (r.recheck_count || 0), 0),
          rows.reduce((s, r) => s + (r.new_commission || 0), 0).toFixed(2),
          rows.reduce((s, r) => s + (r.existing_commission || 0), 0).toFixed(2),
          rows.reduce((s, r) => s + (r.recheck_commission || 0), 0).toFixed(2), total.toFixed(2)],
      ],
    );
    toast.success('Summary CSV dimuat turun');
  };

  const exportDetail = () => {
    if (!details.length) { toast.error('Tiada data untuk export'); return; }
    downloadCSV(
      `commission-detail_${slug}.csv`,
      ['Date', 'Client', 'Closed By', 'Client Status', 'Install Status', 'Product', 'Broker', 'Deposit', 'Commission RM'],
      details.map((d) => [d.date, d.client_name, d.closed_by, d.client_status, d.install_status || '', d.product, d.broker, d.deposit, (d.commission || 0).toFixed(2)]),
    );
    toast.success('Detail CSV dimuat turun');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-[#0a0a0f] border-[#1a1a25] text-white" data-testid="commission-modal">
        <DialogHeader>
          <DialogTitle className="text-sm font-bold flex items-center gap-2">
            <DollarSign size={15} className="text-[#10b981]" /> Commission Board
          </DialogTitle>
        </DialogHeader>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={month}
            onChange={(e) => { setMonth(e.target.value); fetchData(e.target.value); }}
            className="h-8 px-3 text-xs bg-[#111118] border border-[#1a1a25] rounded-lg text-white outline-none cursor-pointer"
            data-testid="commission-month-select"
          >
            <option value="">All Time</option>
            {Array.from({ length: 12 }).map((_, i) => {
              const now = new Date();
              const m = format(new Date(now.getFullYear(), now.getMonth() - i, 1), 'yyyy-MM');
              return <option key={m} value={m}>{monthLabel(m)}</option>;
            })}
          </select>
          <Button size="sm" variant="outline" className="h-8 text-[10px]" onClick={exportSummary} data-testid="commission-export-summary">
            <Download size={12} className="mr-1" /> Summary CSV
          </Button>
          <Button size="sm" variant="outline" className="h-8 text-[10px]" onClick={exportDetail} data-testid="commission-export-detail">
            <Download size={12} className="mr-1" /> Detail CSV
          </Button>
        </div>
        <p className="text-[9px] text-[hsl(var(--text-muted))] uppercase tracking-widest">
          RM{rates.new_client} New Client · RM{rates.existing_client} Existing Client · RM{rates.recheck_tv ?? 5} Recheck TV{month ? ` — ${monthLabel(month)}` : ''}
        </p>
        <div className="space-y-2 mt-2 max-h-[55vh] overflow-y-auto">
          {loading && <div className="py-8 text-center"><Loader2 size={18} className="animate-spin text-[#10b981] mx-auto" /></div>}
          {!loading && rows.length === 0 && (
            <p className="text-[11px] text-[hsl(var(--text-muted))] py-8 text-center" data-testid="commission-empty">
              No commission data{month ? ` for ${monthLabel(month)}` : ''}
            </p>
          )}
          {!loading && rows.map((item, i) => (
            <div key={item.admin} className="flex items-center justify-between px-4 py-3 rounded-xl bg-[#111118] border border-[#1a1a25] hover:border-[#2a2a35] transition-colors" data-testid={`commission-row-${item.admin}`}>
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold ${i === 0 ? 'bg-gradient-to-br from-[#E63946]/30 to-[#F97316]/20 text-[#E63946] border border-[#E63946]/40' : 'bg-[hsl(var(--surface-elevated))] text-[hsl(var(--text-muted))] border border-[hsl(var(--border))]'}`}>{i + 1}</div>
                <div>
                  <p className="text-[12px] font-semibold text-white">{item.admin}</p>
                  <p className="text-[9px] text-[hsl(var(--text-muted))]">
                    {item.count} closed · <span className="text-[#10b981]">{item.new_count} new</span> · <span className="text-[#3b82f6]">{item.existing_count} existing</span>
                    {(item.recheck_count || 0) > 0 && <> · <span className="text-[#f59e0b]">{item.recheck_count} recheck tv</span></>}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-lg font-black font-mono text-[#10b981]">RM{(item.commission || 0).toFixed(2)}</p>
                <p className="text-[8px] text-[hsl(var(--text-muted))] font-mono">
                  {(item.new_commission || 0).toFixed(2)} + {(item.existing_commission || 0).toFixed(2)}
                  {(item.recheck_commission || 0) > 0 ? ` + ${(item.recheck_commission || 0).toFixed(2)}` : ''}
                </p>
              </div>
            </div>
          ))}
          {!loading && rows.length > 0 && (
            <div className="flex items-center justify-between px-4 py-3 rounded-xl bg-[#10b981]/10 border border-[#10b981]/30 mt-2" data-testid="commission-total">
              <span className="text-[10px] font-bold text-[#10b981] uppercase tracking-wider">Total Commission</span>
              <span className="text-lg font-black font-mono text-[#10b981]">RM{total.toFixed(2)}</span>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
